/* Booking host for the Store Platform runtime `<bb-booking>` (frontend integration contract V1, ICR-003).
   The theme owns presentation (dialog / expandable PDP zone / inline page), explicit Shopify context, mount
   configuration and the static fallback. The runtime (Gadget-hosted, `<origin>/runtime/v1/bb-booking.js`) owns
   booking state, API calls, dedupe, the confirmed state and every `appointment_*` dataLayer event — the theme
   pushes none of them. Appointment management is Gadget-hosted (ICR-002): no management mode, credential or URL here.
   Contract: docs/store-platform-integration.md */
(function () {
  'use strict';
  if (window.__bbAppointmentInitialized) return;
  window.__bbAppointmentInitialized = true;
  const BB = window.BB;
  const RUNTIME_TAG = 'bb-booking';
  const CONTRACT_VERSION = '1';
  /* Frozen public source surfaces; anything else becomes `general`. */
  const SOURCE_SURFACES = ['pdp', 'brand_page', 'collection_page', 'store_page', 'store_locator', 'general'];
  /* The only data-* attributes the frozen contract reads (runtime `readMountContext`). Nothing else reaches the element.
     `product_classification` (V1.1, additive) is passed as data-product-classification and omitted when blank — never inferred. */
  const CONTEXT_KEYS = ['source_surface', 'placement', 'presentation', 'environment', 'contract_version', 'store_slug', 'store_name',
    'product_id', 'product_gid', 'product_handle', 'product_title', 'product_type', 'product_classification', 'product_url', 'vendor', 'brand', 'tag', 'variant_id',
    'category', 'campaign', 'page_url'];
  let runtimePromise = null;

  function pageContext() {
    const el = document.getElementById('bb-page-context');
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  }

  /* Locator/URL context (?productGid=&brand=): keeps PDP context when the customer continues via the store finder.
     Product identity is the Shopify product GID (contract key product_gid); a handle is never read from the URL. */
  function urlContext() {
    const out = {};
    try {
      const q = new URLSearchParams(location.search);
      const gid = q.get('productGid');
      if (gid && /^gid:\/\/shopify\/Product\/[0-9]{1,20}$/.test(gid)) out.product_gid = gid;
      if (q.get('brand')) out.brand = q.get('brand');
    } catch (e) { /* ignore */ }
    return out;
  }

  /* Trigger datasets arrive camelCase (data-store-slug → storeSlug); the context works in snake_case. */
  function snake(obj) {
    const out = {};
    Object.keys(obj || {}).forEach((k) => { out[k.replace(/[A-Z]/g, (m) => '_' + m.toLowerCase())] = obj[k]; });
    return out;
  }

  function buildContext(base, overrides) {
    const merged = Object.assign({ source_surface: 'general', page_url: location.href }, pageContext(), urlContext(), snake(base), overrides || {});
    if (!SOURCE_SURFACES.includes(merged.source_surface)) merged.source_surface = 'general';
    if (merged.source_surface === 'pdp') {
      const selectorRoot = document.querySelector('.bb-product[data-product-id][data-variant-choice-required="true"]');
      if (selectorRoot) {
        if (selectorRoot.dataset.selectedVariantId) merged.variant_id = selectorRoot.dataset.selectedVariantId;
        else delete merged.variant_id;
      }
    }
    merged.environment = (BB.config.platform && BB.config.platform.environment) || 'production';
    merged.contract_version = CONTRACT_VERSION;
    const ctx = {};
    CONTEXT_KEYS.forEach((k) => { if (merged[k] != null && merged[k] !== '') ctx[k] = String(merged[k]); });
    return ctx;
  }

  /* Loads the runtime once, only when a mount needs it (never sitewide). Resolves when `<bb-booking>` is defined. */
  function loadRuntime() {
    if (runtimePromise) return runtimePromise;
    const url = BB.config.platform && BB.config.platform.runtimeUrl;
    runtimePromise = new Promise((resolve, reject) => {
      if (customElements.get(RUNTIME_TAG)) return resolve();
      const timeout = setTimeout(() => reject(new Error('runtime-timeout')), ((BB.config.platform && BB.config.platform.timeoutMs) || 5000) * 2);
      customElements.whenDefined(RUNTIME_TAG).then(() => { clearTimeout(timeout); resolve(); }, reject);
      if (!url) { clearTimeout(timeout); return reject(new Error('runtime-not-configured')); }
      if (document.querySelector('script[data-bb-runtime]')) return;
      const s = document.createElement('script');
      s.src = url; s.defer = true; s.setAttribute('data-bb-runtime', '');
      s.onerror = () => { clearTimeout(timeout); reject(new Error('runtime-load-failed')); };
      document.head.appendChild(s);
    });
    runtimePromise.catch(() => { runtimePromise = null; });
    return runtimePromise;
  }

  function toAttr(key) { return 'data-' + key.replace(/_/g, '-'); }

  function states(root) {
    return {
      host: root.querySelector('[data-appointment-host]'),
      loading: root.querySelector('[data-appointment-loading]'),
      fallback: root.querySelector('[data-appointment-fallback]')
    };
  }

  /* Theme-native fallback: runtime removed, static phone/store/contact block shown. No second booking engine. */
  function showFallback(root) {
    const s = states(root);
    if (s.loading) s.loading.hidden = true;
    if (s.host) { s.host.innerHTML = ''; s.host.hidden = true; }
    if (s.fallback) s.fallback.hidden = false;
  }

  /* Only a fatal runtime error replaces the runtime; recoverable errors are rendered and retried by the runtime itself. */
  function watchFatal(root, booking) {
    booking.addEventListener('bb-booking:error', (e) => { if (e.detail && e.detail.fatal === true) showFallback(root); });
  }

  /* Mounts a fresh `<bb-booking>` into the host. Returns a promise of the runtime element. */
  function mount(root, ctx, opts) {
    opts = opts || {};
    const s = states(root);
    if (!s.host) return Promise.reject(new Error('no-host'));
    s.host.innerHTML = '';
    s.host.hidden = false;
    if (s.fallback) s.fallback.hidden = true;
    if (s.loading) s.loading.hidden = false;
    return loadRuntime().then(() => {
      const booking = document.createElement(RUNTIME_TAG);
      Object.keys(ctx).forEach((k) => booking.setAttribute(toAttr(k), ctx[k]));
      watchFatal(root, booking);
      s.host.appendChild(booking);
      if (s.loading) s.loading.hidden = true;
      if (opts.focus !== false) {
        const first = booking.querySelector('input, button, select, [tabindex]');
        if (first) first.focus();
      }
      return booking;
    }).catch((err) => { showFallback(root); throw err; });
  }

  function contextLine(ctx) {
    const parts = [];
    if (ctx.product_title) parts.push(ctx.product_title);
    else if (ctx.brand) parts.push(ctx.brand);
    if (ctx.campaign) parts.push(ctx.campaign);
    if (ctx.store_name) parts.push(ctx.store_name);
    return parts.join(' · ');
  }

  /* Global dialog host: handles every [data-bb-appointment] trigger; expandable zones mount in place instead.
     Nothing loads or mounts before the customer clicks. */
  class BBAppointment extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.host = this.querySelector('[data-appointment-host]');
      this.contextEl = this.querySelector('[data-appointment-context]');
      this.onClick = this.onClick.bind(this);
      document.addEventListener('click', this.onClick);
      this.dialog.addEventListener('bb:dialog:close', () => { this.host.innerHTML = ''; });
    }
    disconnectedCallback() { document.removeEventListener('click', this.onClick); }
    onClick(e) {
      const trigger = e.target.closest('[data-bb-appointment]');
      if (!trigger) return;
      e.preventDefault();
      const zone = trigger.dataset.presentation === 'expandable' ? trigger.closest('[data-appointment-expandable]') : null;
      const trackingContext = buildContext(trigger.dataset, {
        presentation: zone && zone.querySelector('[data-appointment-host]') ? 'expandable' : 'dialog'
      });
      try {
        BB.track('bb_appointment_cta_clicked', {
          source_surface: trackingContext.source_surface,
          placement: trackingContext.placement,
          presentation: trackingContext.presentation,
          product_id: trackingContext.product_id,
          store_slug: trackingContext.store_slug
        });
      } catch (error) {}
      if (zone && zone.querySelector('[data-appointment-host]')) this.expand(zone, trigger);
      else this.open(buildContext(trigger.dataset, { presentation: 'dialog' }));
    }
    open(ctx) {
      const line = contextLine(ctx);
      this.contextEl.textContent = line;
      this.contextEl.hidden = !line;
      BB.openDialog(this.dialog);
      mount(this, ctx).catch(() => {});
    }
    expand(zone, trigger) {
      const runtime = zone.querySelector('.bb-appointment-zone__runtime');
      if (runtime && !runtime.hidden && runtime.querySelector(RUNTIME_TAG)) {
        const first = runtime.querySelector('input, button, select, [tabindex]');
        if (first) first.focus();
        return;
      }
      const ctx = buildContext(trigger.dataset, { presentation: 'expandable' });
      if (runtime) runtime.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      zone.classList.add('is-expanded');
      mount(zone, ctx).catch(() => {});
    }
  }
  if (!customElements.get('bb-appointment')) customElements.define('bb-appointment', BBAppointment);

  /* Inline presentation (general booking page, store page): `<bb-booking>` is in the markup and the runtime script is
     deferred by the section; this host only guards the outcome — element never defined (404/time-out) or fatal error →
     fallback. An optional [data-appointment-loading] is shown only until the runtime is defined (never a permanent spinner). */
  class BBAppointmentInline extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('data-bb-defer-inline')) {
        this.activate();
        return;
      }
      /* An off-screen custom element can be focused by its remote runtime on initial load.
         Keep the element inert inside a template until its section is actually in view. */
      if (!this.querySelector('template[data-bb-inline-booking-template]')) {
        this.activate();
        return;
      }
      if (!('IntersectionObserver' in window)) {
        this.activate();
        return;
      }
      this._inlineObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this._inlineObserver.disconnect();
        this._inlineObserver = null;
        this.activate();
      }, { threshold: 0 });
      this._inlineObserver.observe(this);
    }
    disconnectedCallback() {
      if (this._inlineObserver) {
        this._inlineObserver.disconnect();
        this._inlineObserver = null;
      }
    }
    activate() {
      if (this._activated) return;
      this._activated = true;
      const template = this.querySelector('template[data-bb-inline-booking-template]');
      const s = states(this);
      if (template && s.host) {
        s.host.appendChild(template.content.cloneNode(true));
        template.remove();
      }
      const booking = this.querySelector(RUNTIME_TAG);
      if (!booking) { showFallback(this); return; }
      watchFatal(this, booking);
      if (s.loading && !customElements.get(RUNTIME_TAG)) s.loading.hidden = false;
      loadRuntime().then(() => { if (s.loading) s.loading.hidden = true; }, () => showFallback(this));
    }
  }
  if (!customElements.get('bb-appointment-inline')) customElements.define('bb-appointment-inline', BBAppointmentInline);
})();
/* BB_BOOKING_SCHEDULER_V2 */
/* BB_BOOKING_UX_V3 */
(function () {
  'use strict';
  if (window.__bbBookingSchedulerThemeV2) return;
  window.__bbBookingSchedulerThemeV2 = true;

  var BB = window.BB || {};
  var DAY_RE = /^(maandag|dinsdag|woensdag|donderdag|vrijdag|zaterdag|zondag)\s+(\d{1,2})\s+([a-zA-Z\u00c0-\u017f]+)(?:\s+(\d{4}))?$/i;
  var ISO_DATE_RE = /\b(\d{4})-(\d{2})-(\d{2})\b/;
  var TIME_RE = /^([01]?\d|2[0-3]):[0-5]\d$/;
  var TIME_FIND_RE = /(?:^|\s)((?:[01]?\d|2[0-3]):[0-5]\d)(?:\s|$)/;
  var MONTHS = { januari: 0, februari: 1, maart: 2, april: 3, mei: 4, juni: 5, juli: 6, augustus: 7, september: 8, oktober: 9, november: 10, december: 11 };
  var states = new WeakMap();

  function decodeEntities(value) {
    var el = document.createElement('textarea');
    el.innerHTML = String(value == null ? '' : value);
    return el.value;
  }
  function uiText(key) {
    var value = typeof BB.t === 'function' ? BB.t(key) : key;
    return decodeEntities(value);
  }
  function text(value) { return String(value || '').replace(/\s+/g, ' ').trim(); }
  function locale() { return (document.documentElement.lang || 'nl-NL').toLowerCase(); }
  function normalizeDatePhrase(value) {
    return text(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[.,\u00a0]/g, ' ').replace(/\s+/g, ' ').trim();
  }
  var DATE_NAME_CACHE = { locale: '', weekdays: [], months: [] };
  function localeDateNames() {
    var lang = locale();
    if (DATE_NAME_CACHE.locale === lang) return DATE_NAME_CACHE;
    var names = { locale: lang, weekdays: [], months: [] };
    try {
      for (var month = 0; month < 12; month += 1) {
        var longMonth = new Intl.DateTimeFormat(lang, { month: 'long' }).format(new Date(2024, month, 15));
        var shortMonth = new Intl.DateTimeFormat(lang, { month: 'short' }).format(new Date(2024, month, 15));
        names.months.push({ index: month, aliases: [normalizeDatePhrase(longMonth), normalizeDatePhrase(shortMonth)] });
      }
      for (var day = 0; day < 7; day += 1) {
        var date = new Date(2024, 0, 7 + day);
        var weekday = new Intl.DateTimeFormat(lang, { weekday: 'long' }).format(date);
        var shortWeekday = new Intl.DateTimeFormat(lang, { weekday: 'short' }).format(date);
        names.weekdays.push(normalizeDatePhrase(weekday), normalizeDatePhrase(shortWeekday));
      }
    } catch (e) { names.months = []; names.weekdays = []; }
    DATE_NAME_CACHE = names;
    return names;
  }
  function localeDateParts(value) {
    var source = normalizeDatePhrase(value);
    var names = localeDateNames();
    if (!source || !names.weekdays.length) return null;
    var padded = ' ' + source + ' ';
    function hasWord(alias) { return alias && alias.length >= 3 && padded.indexOf(' ' + alias + ' ') !== -1; }
    if (!names.weekdays.some(hasWord)) return null;
    var monthEntry = names.months.find(function (entry) { return entry.aliases.some(hasWord); });
    if (!monthEntry) return null;
    var tokens = source.match(/(?:^|\s)(\d{1,4})(?=\s|$)/g) || [];
    var numbers = tokens.map(function (part) { return Number(part.trim()); });
    var day = numbers.find(function (number) { return number >= 1 && number <= 31; });
    var year = numbers.find(function (number) { return number >= 1900 && number <= 2200; });
    if (!day) return null;
    var check = new Date(year || 2024, monthEntry.index, day);
    if (check.getMonth() !== monthEntry.index || check.getDate() !== day) return null;
    return { day: day, month: monthEntry.index, year: year || null };
  }
  function inSchedulerUi(el) { return !!(el && el.closest && el.closest('[data-bb-scheduler-ui]')); }
  function firstAttribute(el, names) {
    if (!el || !el.getAttribute) return '';
    for (var i = 0; i < names.length; i += 1) {
      var value = text(el.getAttribute(names[i]));
      if (value) return value;
    }
    return '';
  }
  function dateSource(el) {
    return firstAttribute(el, ['data-date', 'datetime', 'data-value', 'aria-label']) || text(el && el.textContent);
  }
  function timeValue(button) {
    var candidates = [
      firstAttribute(button, ['data-time']),
      text(button && button.value),
      firstAttribute(button, ['aria-label']),
      text(button && button.textContent)
    ];
    for (var i = 0; i < candidates.length; i += 1) {
      var candidate = candidates[i];
      if (!candidate) continue;
      if (TIME_RE.test(candidate)) return candidate;
      var match = candidate.match(TIME_FIND_RE);
      if (match) return match[1];
    }
    return '';
  }
  function controlLabel(el) {
    return firstAttribute(el, ['aria-label', 'title']) || text(el && el.textContent);
  }

  function documentOrder(a, b) {
    if (a === b) return 0;
    var pos = a.compareDocumentPosition(b);
    if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
    return 0;
  }

  function ensureShadowTheme(root) {
    if (typeof ShadowRoot === 'undefined' || !(root instanceof ShadowRoot)) return;
    if (root.querySelector('link[data-bb-booking-theme]')) return;
    var source = document.querySelector('link[href*="bb-appointment.css"]');
    if (!source || !source.href) return;
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = source.href;
    link.setAttribute('data-bb-booking-theme', '');
    root.appendChild(link);
  }

  function dateLabels(root) {
    var candidates = Array.prototype.filter.call(root.querySelectorAll('h1,h2,h3,h4,h5,h6,legend,strong,b,p,span,div,time,[data-date],[datetime]'), function (el) {
      if (inSchedulerUi(el) || el.querySelector('button')) return false;
      var source = dateSource(el);
      return ISO_DATE_RE.test(source) || DAY_RE.test(source.toLowerCase()) || !!localeDateParts(source);
    });
    return candidates.filter(function (el) {
      return !candidates.some(function (other) { return other !== el && el.contains(other); });
    }).sort(documentOrder);
  }

  function timeButtons(root) {
    return Array.prototype.filter.call(root.querySelectorAll('button'), function (button) {
      return !inSchedulerUi(button) && !!timeValue(button);
    }).sort(documentOrder);
  }

  function inferFirstDate(day, month, explicitYear) {
    if (explicitYear) return new Date(Number(explicitYear), month, day, 12, 0, 0, 0);
    var now = new Date();
    var today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0, 0);
    var years = [today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1];
    var future = years.map(function (year) { return new Date(year, month, day, 12, 0, 0, 0); })
      .filter(function (date) { return date.getTime() >= today.getTime() - 86400000; })
      .sort(function (a, b) { return a.getTime() - b.getTime(); });
    return future.length ? future[0] : new Date(today.getFullYear(), month, day, 12, 0, 0, 0);
  }

  function parseGroupDate(labelText, previousDate) {
    var source = text(labelText);
    var iso = source.match(ISO_DATE_RE);
    if (iso) {
      var isoDate = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]), 12, 0, 0, 0);
      if (!Number.isNaN(isoDate.getTime())) return isoDate;
    }
    var match = source.toLowerCase().match(DAY_RE);
    var translated = match ? null : localeDateParts(source);
    if (!match && !translated) return null;
    var day = match ? Number(match[2]) : translated.day;
    var month = match ? MONTHS[match[3]] : translated.month;
    var explicitYear = match ? match[4] : translated.year;
    if (month == null) return null;
    var date;
    if (!previousDate) {
      date = inferFirstDate(day, month, explicitYear);
    } else {
      var year = explicitYear ? Number(explicitYear) : previousDate.getFullYear();
      date = new Date(year, month, day, 12, 0, 0, 0);
      if (!explicitYear && date.getTime() < previousDate.getTime() - 86400000 * 7) date = new Date(year + 1, month, day, 12, 0, 0, 0);
    }
    return date;
  }

  function keyForDate(date) {
    return date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');
  }

  function collectGroups(root) {
    var labels = dateLabels(root);
    var buttons = timeButtons(root);
    if (!labels.length || !buttons.length) return [];
    var stream = labels.map(function (el) { return { type: 'date', el: el }; })
      .concat(buttons.map(function (el) { return { type: 'time', el: el }; }))
      .sort(function (a, b) { return documentOrder(a.el, b.el); });
    var groups = [];
    var current = null;
    stream.forEach(function (item) {
      if (item.type === 'date') {
        current = { label: item.el, labelText: dateSource(item.el), buttons: [] };
        groups.push(current);
      } else if (current) current.buttons.push(item.el);
    });
    groups = groups.filter(function (group) { return group.buttons.length > 0; });
    var previousDate = null;
    groups.forEach(function (group) {
      group.date = parseGroupDate(group.labelText, previousDate);
      if (group.date) {
        group.key = keyForDate(group.date);
        previousDate = group.date;
      }
    });
    return groups.filter(function (group) { return !!group.date; });
  }

  function groupContainer(group, labels, root) {
    var node = group.label;
    while (node.parentElement && node.parentElement !== root) {
      var parent = node.parentElement;
      var labelCount = labels.filter(function (label) { return parent.contains(label); }).length;
      var containsButtons = group.buttons.every(function (button) { return parent.contains(button); });
      if (labelCount !== 1 || !containsButtons) break;
      node = parent;
    }
    return node;
  }

  function isSelected(button) {
    var cls = typeof button.className === 'string' ? button.className : '';
    return button.getAttribute('aria-pressed') === 'true' ||
      button.getAttribute('aria-selected') === 'true' ||
      button.hasAttribute('data-selected') ||
      /(^|\s)(is-)?(selected|active)(\s|$)/i.test(cls);
  }
  function isDisabled(button) { return button.disabled || button.getAttribute('aria-disabled') === 'true'; }

  function selectedGroupKey(groups, previousKey) {
    var selected = groups.find(function (group) { return group.buttons.some(isSelected); });
    if (selected) return selected.key;
    if (previousKey && groups.some(function (group) { return group.key === previousKey; })) return previousKey;
    return groups[0].key;
  }

  function startOfWeek(date) {
    var d = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);
    var offset = (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - offset);
    return d;
  }
  function addDays(date, days) { return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days, 12, 0, 0, 0); }
  function weekKey(date) { return keyForDate(startOfWeek(date)); }

  function availableWeeks(groups) {
    var first = startOfWeek(groups[0].date);
    var last = startOfWeek(groups[groups.length - 1].date);
    var weeks = [];
    for (var cursor = first; cursor.getTime() <= last.getTime(); cursor = addDays(cursor, 7)) weeks.push(new Date(cursor.getTime()));
    return weeks;
  }

  function currentWeekIndex(weeks, selectedKey, previousIndex) {
    var selectedGroupDate = selectedKey ? new Date(selectedKey + 'T12:00:00') : null;
    if (selectedGroupDate && !Number.isNaN(selectedGroupDate.getTime())) {
      var selectedWeek = weekKey(selectedGroupDate);
      var index = weeks.findIndex(function (week) { return weekKey(week) === selectedWeek; });
      if (index >= 0) return index;
    }
    if (typeof previousIndex === 'number' && weeks[previousIndex]) return previousIndex;
    return 0;
  }

  function groupInWeek(group, weekStart) {
    var start = weekStart.getTime();
    var end = addDays(weekStart, 7).getTime();
    var time = group.date.getTime();
    return time >= start && time < end;
  }
  function firstAvailableInWeek(groups, weekStart) { return groups.find(function (group) { return groupInWeek(group, weekStart); }) || null; }
  function nextAvailableAfter(groups, weekStart) {
    var end = addDays(weekStart, 7).getTime();
    return groups.find(function (group) { return group.date.getTime() >= end; }) || null;
  }

  function formatWeekRange(start) {
    var end = addDays(start, 6);
    var sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
    var sameYear = start.getFullYear() === end.getFullYear();
    if (sameMonth) return start.getDate() + ' – ' + end.getDate() + ' ' + new Intl.DateTimeFormat(locale(), { month: 'long' }).format(end);
    if (sameYear) return new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'short' }).format(start) + ' – ' + new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'short' }).format(end);
    return new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'short', year: 'numeric' }).format(start) + ' – ' + new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'short', year: 'numeric' }).format(end);
  }

  function formatFullDate(date) { return new Intl.DateTimeFormat(locale(), { weekday: 'long', day: 'numeric', month: 'long' }).format(date); }
  function formatCompactDate(date) { return new Intl.DateTimeFormat(locale(), { weekday: 'short', day: 'numeric', month: 'short' }).format(date).replace('.', ''); }
  function formatWeekday(date) { return new Intl.DateTimeFormat(locale(), { weekday: 'short' }).format(date).replace('.', ''); }

  function createButton(className, label, ariaLabel) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.textContent = label;
    if (ariaLabel) button.setAttribute('aria-label', ariaLabel);
    return button;
  }

  function signature(groups) {
    return groups.map(function (group) {
      return group.key + ':' + group.buttons.map(function (button) {
        return timeValue(button) + ':' + (isDisabled(button) ? '0' : '1') + ':' + (isSelected(button) ? '1' : '0');
      }).join(',');
    }).join('|');
  }

  function hideOriginal(groups, root, state) {
    var labels = groups.map(function (group) { return group.label; });
    state.hidden = [];
    groups.forEach(function (group) {
      var container = groupContainer(group, labels, root);
      if (container && !inSchedulerUi(container)) {
        container.classList.add('bb-booking-original-day');
        state.hidden.push(container);
      }
    });
  }

  function restoreOriginal(state) {
    (state.hidden || []).forEach(function (el) { if (el && el.classList) el.classList.remove('bb-booking-original-day'); });
    state.hidden = [];
  }

  function restoreRuntimeControls(state) {
    (state.runtimeControls || []).forEach(function (entry) {
      var el = entry.el;
      if (!el || !el.classList) return;
      el.classList.remove('bb-booking-runtime-control--hidden');
      if (entry.ariaHidden == null) el.removeAttribute('aria-hidden');
      else el.setAttribute('aria-hidden', entry.ariaHidden);
      if (entry.tabindex == null) el.removeAttribute('tabindex');
      else el.setAttribute('tabindex', entry.tabindex);
    });
    if (state.runtimeControlContainer && state.runtimeControlContainer.classList) {
      state.runtimeControlContainer.classList.remove('bb-booking-runtime-controls--hidden');
    }
    state.runtimeControls = [];
    state.runtimeControlContainer = null;
  }

  function hideRuntimeControls(root, state) {
    restoreRuntimeControls(state);
    var labels = ['vorige periode', 'volgende periode', 'verder', 'previous period', 'next period', 'next', 'continue'];
    var controls = Array.prototype.filter.call(root.querySelectorAll('button'), function (button) {
      if (inSchedulerUi(button)) return false;
      return labels.indexOf(controlLabel(button).toLowerCase()) !== -1;
    });
    state.runtimeControls = controls.map(function (button) {
      return {
        el: button,
        ariaHidden: button.getAttribute('aria-hidden'),
        tabindex: button.getAttribute('tabindex')
      };
    });
    controls.forEach(function (button) {
      button.classList.add('bb-booking-runtime-control--hidden');
      button.setAttribute('aria-hidden', 'true');
      button.setAttribute('tabindex', '-1');
    });

    if (controls.length > 1) {
      var parent = controls[0].parentElement;
      var sameParent = parent && controls.every(function (button) { return button.parentElement === parent; });
      if (sameParent) {
        var clone = parent.cloneNode(true);
        clone.querySelectorAll('button').forEach(function (button) { button.remove(); });
        if (!text(clone.textContent)) {
          parent.classList.add('bb-booking-runtime-controls--hidden');
          state.runtimeControlContainer = parent;
        }
      }
    }
  }

  function simplifyContext(value) {
    var firstPart = text(value).split('·')[0].trim();
    var match = firstPart.match(/^(.*?)\s+bij\s+(Beds & Bedding.+)$/i);
    if (match) return match[1].trim() + ' · ' + match[2].trim();
    return firstPart;
  }

  function restoreRuntimeDecorations(state) {
    if (state.progress && state.progress.isConnected) state.progress.remove();
    state.progress = null;
    if (state.contextElement && state.contextElement.isConnected && state.contextOriginalText != null) {
      state.contextElement.textContent = state.contextOriginalText;
      state.contextElement.removeAttribute('data-bb-booking-context-line');
    }
    state.contextElement = null;
    state.contextOriginalText = null;
  }

  function decorateRuntime(root, state) {
    var headings = Array.prototype.filter.call(root.querySelectorAll('h1,h2,h3,h4,h5,h6'), function (el) { return !inSchedulerUi(el); });
    var stepTitle = headings.find(function (el) {
      var value = text(el.textContent).toLowerCase();
      return value.indexOf('datum') >= 0 && value.indexOf('tijd') >= 0;
    });
    if (stepTitle) {
      stepTitle.setAttribute('data-bb-booking-step-title', '');
      if (!state.progress || !state.progress.isConnected) {
        var progress = document.createElement('p');
        progress.className = 'bb-booking-progress';
        progress.setAttribute('data-bb-booking-progress', '');
        progress.textContent = uiText('bookingProgressDateTime');
        stepTitle.parentNode.insertBefore(progress, stepTitle);
        state.progress = progress;
      }
    }

    var candidates = Array.prototype.filter.call(root.querySelectorAll('p,div,span'), function (el) {
      if (inSchedulerUi(el) || el.querySelector('button')) return false;
      var value = text(el.textContent);
      return value.length > 20 && value.length < 220 && value.indexOf('Beds & Bedding') >= 0 && value.indexOf('·') >= 0;
    });
    var context = candidates.find(function (el) {
      return !candidates.some(function (other) { return other !== el && el.contains(other); });
    });
    if (context) {
      if (state.contextElement !== context) {
        state.contextElement = context;
        state.contextOriginalText = text(context.textContent);
      }
      context.setAttribute('data-bb-booking-context-line', '');
      context.textContent = simplifyContext(state.contextOriginalText || context.textContent);
    }
  }

  function runtimeContinueButton(root) {
    var labels = [uiText('bookingContinue'), 'Verder', 'Volgende', 'Next', 'Continue']
      .map(function (label) { return text(label).toLowerCase(); })
      .filter(function (label, index, list) { return label && list.indexOf(label) === index; });
    return Array.prototype.find.call(root.querySelectorAll('button'), function (button) {
      if (inSchedulerUi(button) || isDisabled(button)) return false;
      return labels.indexOf(controlLabel(button).toLowerCase()) !== -1;
    }) || null;
  }

  function advanceRuntime(root, state, continueButton) {
    if (!state.pending || !state.pending.original || isDisabled(state.pending.original)) return;
    continueButton.setAttribute('aria-busy', 'true');
    var attempts = 0;

    function attempt() {
      if (!state.pending || !state.pending.original || isDisabled(state.pending.original)) {
        continueButton.removeAttribute('aria-busy');
        return;
      }

      if (!isSelected(state.pending.original)) state.pending.original.click();

      var nativeContinue = runtimeContinueButton(root);
      if (nativeContinue && !isDisabled(nativeContinue)) {
        nativeContinue.click();
        window.setTimeout(function () { continueButton.removeAttribute('aria-busy'); }, 300);
        return;
      }

      attempts += 1;
      if (attempts < 6) {
        window.setTimeout(attempt, 60);
        return;
      }
      continueButton.removeAttribute('aria-busy');
    }

    attempt();
  }

  function setPendingSelection(state, group, original, proxy, timeGrid, footer, summary) {
    state.pending = { dateKey: group.key, time: timeValue(original), original: original };
    timeGrid.querySelectorAll('.bb-booking-scheduler__time').forEach(function (button) {
      button.classList.remove('is-selected');
      button.setAttribute('aria-pressed', 'false');
    });
    proxy.classList.add('is-selected');
    proxy.setAttribute('aria-pressed', 'true');
    summary.textContent = formatCompactDate(group.date) + ' · ' + state.pending.time;
    footer.hidden = false;
  }

  function renderScheduler(booking, root, groups, state) {
    restoreOriginal(state);
    restoreRuntimeControls(state);
    if (state.ui && state.ui.isConnected) state.ui.remove();

    state.selectedKey = selectedGroupKey(groups, state.selectedKey);
    var selectedGroup = groups.find(function (group) { return group.key === state.selectedKey; }) || groups[0];
    var weeks = availableWeeks(groups);
    state.weekIndex = currentWeekIndex(weeks, state.selectedKey, state.weekIndex);
    var visibleWeek = weeks[state.weekIndex] || weeks[0];
    if (!visibleWeek) return;

    decorateRuntime(root, state);

    var ui = document.createElement('section');
    ui.className = 'bb-booking-scheduler';
    ui.setAttribute('data-bb-scheduler-ui', '');
    ui.setAttribute('aria-label', uiText('bookingAvailableTimes'));

    var datePane = document.createElement('div');
    datePane.className = 'bb-booking-scheduler__date';

    var weekHeader = document.createElement('div');
    weekHeader.className = 'bb-booking-scheduler__week-header';
    var previous = createButton('bb-booking-scheduler__nav', '‹', uiText('bookingPreviousWeek'));
    previous.disabled = state.weekIndex <= 0;
    var weekTitle = document.createElement('h3');
    weekTitle.className = 'bb-booking-scheduler__week-title';
    weekTitle.textContent = formatWeekRange(visibleWeek);
    var next = createButton('bb-booking-scheduler__nav', '›', uiText('bookingNextWeek'));
    next.disabled = state.weekIndex >= weeks.length - 1;
    weekHeader.append(previous, weekTitle, next);

    var dayStrip = document.createElement('div');
    dayStrip.className = 'bb-booking-scheduler__days';
    var today = new Date();
    var todayKey = keyForDate(new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0, 0, 0));

    for (var dayIndex = 0; dayIndex < 7; dayIndex += 1) {
      var date = addDays(visibleWeek, dayIndex);
      var key = keyForDate(date);
      var group = groups.find(function (candidate) { return candidate.key === key; });
      var dayEl;
      if (group) {
        dayEl = createButton('bb-booking-scheduler__day is-available', '', formatFullDate(date));
        dayEl.setAttribute('data-date-key', key);
        dayEl.setAttribute('aria-pressed', key === state.selectedKey ? 'true' : 'false');
        if (key === state.selectedKey) dayEl.classList.add('is-selected');
        dayEl.addEventListener('click', function (event) {
          state.selectedKey = event.currentTarget.getAttribute('data-date-key');
          state.pending = null;
          renderScheduler(booking, root, groups, state);
        });
      } else {
        dayEl = document.createElement('span');
        dayEl.className = 'bb-booking-scheduler__day is-unavailable';
        dayEl.setAttribute('aria-disabled', 'true');
        dayEl.setAttribute('aria-label', formatFullDate(date));
      }
      if (key === todayKey) dayEl.classList.add('is-today');
      var weekday = document.createElement('span');
      weekday.className = 'bb-booking-scheduler__weekday';
      weekday.textContent = key === todayKey ? uiText('bookingToday') : formatWeekday(date);
      var number = document.createElement('span');
      number.className = 'bb-booking-scheduler__day-number';
      number.textContent = String(date.getDate());
      dayEl.append(weekday, number);
      dayStrip.appendChild(dayEl);
    }

    previous.addEventListener('click', function () {
      if (state.weekIndex <= 0) return;
      state.weekIndex -= 1;
      var first = firstAvailableInWeek(groups, weeks[state.weekIndex]);
      if (first) state.selectedKey = first.key;
      state.pending = null;
      renderScheduler(booking, root, groups, state);
    });
    next.addEventListener('click', function () {
      if (state.weekIndex >= weeks.length - 1) return;
      state.weekIndex += 1;
      var first = firstAvailableInWeek(groups, weeks[state.weekIndex]);
      if (first) state.selectedKey = first.key;
      state.pending = null;
      renderScheduler(booking, root, groups, state);
    });
    datePane.append(weekHeader, dayStrip);

    var timesPane = document.createElement('div');
    timesPane.className = 'bb-booking-scheduler__times';
    selectedGroup = groups.find(function (group) { return group.key === state.selectedKey; }) || firstAvailableInWeek(groups, visibleWeek);

    if (selectedGroup) {
      state.selectedKey = selectedGroup.key;
      var selectedTitle = document.createElement('h3');
      selectedTitle.className = 'bb-booking-scheduler__date-title';
      selectedTitle.textContent = formatFullDate(selectedGroup.date);

      var hint = document.createElement('p');
      hint.className = 'bb-booking-scheduler__time-hint';
      hint.textContent = uiText('bookingChooseTime');

      var timeGrid = document.createElement('div');
      timeGrid.className = 'bb-booking-scheduler__time-grid';

      var footer = document.createElement('div');
      footer.className = 'bb-booking-scheduler__footer';
      footer.hidden = true;

      var summary = document.createElement('div');
      summary.className = 'bb-booking-scheduler__selection';

      var continueButton = createButton('bb-btn bb-booking-scheduler__continue', uiText('bookingContinue'));
      continueButton.addEventListener('click', function () {
        advanceRuntime(root, state, continueButton);
      });

      selectedGroup.buttons.forEach(function (original) {
        var displayedTime = timeValue(original);
        if (!displayedTime) return;
        var proxy = createButton('bb-booking-scheduler__time', displayedTime, displayedTime);
        proxy.disabled = isDisabled(original);
        var pendingMatch = state.pending && state.pending.dateKey === selectedGroup.key && state.pending.time === displayedTime;
        if (pendingMatch || isSelected(original)) {
          proxy.classList.add('is-selected');
          proxy.setAttribute('aria-pressed', 'true');
          state.pending = { dateKey: selectedGroup.key, time: displayedTime, original: original };
          summary.textContent = formatCompactDate(selectedGroup.date) + ' · ' + displayedTime;
          footer.hidden = false;
        } else proxy.setAttribute('aria-pressed', 'false');

        proxy.addEventListener('click', function () {
          if (proxy.disabled) return;
          setPendingSelection(state, selectedGroup, original, proxy, timeGrid, footer, summary);
        });
        timeGrid.appendChild(proxy);
      });

      footer.append(summary, continueButton);
      timesPane.append(selectedTitle, hint, timeGrid, footer);
    } else {
      var empty = document.createElement('div');
      empty.className = 'bb-booking-scheduler__empty';
      var emptyText = document.createElement('p');
      emptyText.textContent = uiText('bookingNoTimesWeek');
      empty.appendChild(emptyText);
      var nextAvailable = nextAvailableAfter(groups, visibleWeek);
      if (nextAvailable) {
        var nextAvailableButton = createButton('bb-btn bb-btn--secondary', uiText('bookingNextAvailable'));
        nextAvailableButton.addEventListener('click', function () {
          state.selectedKey = nextAvailable.key;
          state.weekIndex = currentWeekIndex(weeks, state.selectedKey, state.weekIndex);
          state.pending = null;
          renderScheduler(booking, root, groups, state);
        });
        empty.appendChild(nextAvailableButton);
      }
      timesPane.appendChild(empty);
    }

    ui.append(datePane, timesPane);
    var groupLabels = groups.map(function (group) { return group.label; });
    var insertionPoint = groupContainer(groups[0], groupLabels, root) || groups[0].label;
    insertionPoint.parentNode.insertBefore(ui, insertionPoint);

    state.ui = ui;
    hideOriginal(groups, root, state);
    hideRuntimeControls(root, state);
    booking.classList.add('bb-booking-scheduler-ready');
    var dialog = booking.closest && booking.closest('.bb-appointment-dialog');
    if (dialog) dialog.classList.add('has-bb-booking-scheduler');

    /* Keep the selected day in the horizontal strip without moving the page vertically.
       scrollIntoView() also scrolls ancestor viewports, including the storefront page on initial inline mount. */
    var activeDay = ui.querySelector('.bb-booking-scheduler__day.is-selected');
    if (activeDay && dayStrip.scrollWidth > dayStrip.clientWidth) {
      var stripRect = dayStrip.getBoundingClientRect();
      var dayRect = activeDay.getBoundingClientRect();
      if (dayRect.left < stripRect.left) dayStrip.scrollLeft += dayRect.left - stripRect.left;
      else if (dayRect.right > stripRect.right) dayStrip.scrollLeft += dayRect.right - stripRect.right;
    }
  }

  function removeScheduler(booking, state) {
    restoreOriginal(state);
    restoreRuntimeControls(state);
    restoreRuntimeDecorations(state);
    if (state.ui && state.ui.isConnected) state.ui.remove();
    state.ui = null;
    state.signature = '';
    state.pending = null;
    booking.classList.remove('bb-booking-scheduler-ready');
    var dialog = booking.closest && booking.closest('.bb-appointment-dialog');
    if (dialog) dialog.classList.remove('has-bb-booking-scheduler');
  }

  function refreshBooking(booking) {
    var state = states.get(booking);
    if (!state || state.rendering || !booking.isConnected) return;
    var root = booking.shadowRoot || booking;
    if (root !== state.root) {
      if (state.observer) state.observer.disconnect();
      state.root = root;
      ensureShadowTheme(root);
      state.observer = new MutationObserver(function () { scheduleRefresh(booking); });
      state.observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'disabled', 'aria-disabled', 'aria-pressed', 'aria-selected'] });
    }

    state.rendering = true;
    try {
      var groups = collectGroups(root);
      if (!groups.length) {
        removeScheduler(booking, state);
        return;
      }
      var nextSignature = signature(groups);
      if (nextSignature === state.signature && state.ui && state.ui.isConnected) return;
      state.signature = nextSignature;
      renderScheduler(booking, root, groups, state);
    } finally {
      state.rendering = false;
    }
  }

  function scheduleRefresh(booking) {
    var state = states.get(booking);
    if (!state || state.scheduled) return;
    state.scheduled = true;
    requestAnimationFrame(function () {
      state.scheduled = false;
      refreshBooking(booking);
    });
  }

  function watchBooking(booking) {
    if (!booking || states.has(booking)) return;
    var state = { root: null, observer: null, ui: null, hidden: [], runtimeControls: [], runtimeControlContainer: null, progress: null, contextElement: null, contextOriginalText: null, selectedKey: '', weekIndex: 0, pending: null, signature: '', rendering: false, scheduled: false };
    states.set(booking, state);
    refreshBooking(booking);
    [50, 150, 300, 600, 1200, 2000, 3500].forEach(function (delay) {
      window.setTimeout(function () { if (booking.isConnected) refreshBooking(booking); }, delay);
    });
  }

  function discover(node) {
    if (!(node instanceof Element)) return;
    if (node.matches('bb-booking')) watchBooking(node);
    node.querySelectorAll('bb-booking').forEach(watchBooking);
  }

  document.querySelectorAll('bb-booking').forEach(watchBooking);
  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) { mutation.addedNodes.forEach(discover); });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

